import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { AuthenticationService } from "./auth.service";

/**
 * Consultation seule.
 *
 * Certains écrans sont ouverts à un rôle sans lui donner le droit d'agir : la
 * trésorerie voit les écrans du contrôle de gestion mais n'y crée, ne modifie
 * ni ne supprime rien. Les routes concernées portent `data.lectureSeulePour`,
 * et les composants partagés (en-tête de page, tableau générique) masquent
 * leurs actions quand le rôle de l'utilisateur y figure.
 *
 * Les écrans où le rôle a déjà des droits d'écriture ne portent simplement pas
 * l'attribut.
 *
 * Les rôles s'additionnent (un utilisateur peut en tenir plusieurs) : une route
 * peut donc lister dans `data.ecriturePour` les rôles qui gardent l'écriture même
 * s'ils tiennent aussi un rôle de `lectureSeulePour` (ex. commercial + responsable
 * de projet). Sans `ecriturePour`, un rôle en lecture seule l'emporte.
 */
@Injectable({ providedIn: "root" })
export class LectureSeuleService {
	private lectureSeule = false;

	constructor(
		private router: Router,
		private authService: AuthenticationService,
	) {
		this.evaluer();
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) this.evaluer();
		});
	}

	/** Vrai si l'écran courant est en consultation seule pour l'utilisateur connecté. */
	get isLectureSeule(): boolean {
		return this.lectureSeule;
	}

	private evaluer(): void {
		if (this.authService.isAdmin()) {
			this.lectureSeule = false;
			return;
		}
		const roles = this.collecterRoles("lectureSeulePour");
		const ecriture = this.collecterRoles("ecriturePour");
		const gardeEcriture = ecriture.length > 0 && this.authService.hasPermission(ecriture);
		this.lectureSeule = roles.length > 0 && this.authService.hasPermission(roles) && !gardeEcriture;
	}

	/** `data` n'est pas hérité des routes parentes par défaut : on parcourt l'arbre. */
	private collecterRoles(cle: "lectureSeulePour" | "ecriturePour"): string[] {
		const roles: string[] = [];
		let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
		while (route) {
			const declares = route.data?.[cle];
			if (Array.isArray(declares)) roles.push(...declares);
			route = route.firstChild;
		}
		return roles;
	}
}
